/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {computed, signal, WritableSignal} from '@angular/core';
import {Observable} from 'rxjs';
import {GridPageRequest} from '../../model/gridPageRequest';
import {PagingResult} from '../../model/pagingResult';

/** What a server-paged grid supplies: how to read a page, the filters on screen, and where the rows go. */
export interface ServerPagingSource<T> {
    fetchPage(request: GridPageRequest): Observable<PagingResult<T>>;
    collectCriteria(): Record<string, string[]>;
    /** Criteria to match within a stored value rather than against it whole; see {@link GridPageRequest}. */
    partialKeys?(): string[];
    onPage(rows: T[], page: PagingResult<T>): void;
    onError?(err: unknown): void;
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [10, 20, 50, 100, 200];

export interface ServerPagingOptions {
    /** Must be one of {@link pageSizeOptions} for the panel's picker to mark it. */
    pageSize?: number;
    pageSizeOptions?: number[];
}

/**
 * Page, sort and filter state for a grid that holds one page of rows and asks the server for each one. AG Grid
 * cannot page such a grid itself - it counts only the rows it holds - so this tracks what the server reported and
 * renders through {@link ServerPagingPanelComponent}.
 */
export class ServerPagingController<T> {
    /** How long filter events are gathered before one request goes out; a filter menu emits several per change. */
    static readonly FILTER_DEBOUNCE_MS = 300;

    readonly pageSizeOptions: number[];
    readonly pageSize: WritableSignal<number>;
    /** Zero-based index of the page on screen. */
    readonly pageIndex = signal(0);
    /** Rows matching the current filters across the whole result, as the server reported. */
    readonly totalRows = signal(0);
    readonly loading = signal(false);

    readonly firstRowOnPage = computed(() => this.totalRows() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1);
    readonly lastRowOnPage = computed(() => Math.min((this.pageIndex() + 1) * this.pageSize(), this.totalRows()));
    /** One page even with nothing on it, so the panel reads "Page 1 of 1" rather than "of 0". */
    readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalRows() / Math.max(this.pageSize(), 1))));
    readonly currentPage = computed(() => this.pageIndex() + 1);
    readonly onFirstPage = computed(() => this.pageIndex() <= 0);
    readonly onLastPage = computed(() => this.pageIndex() >= this.totalPages() - 1);

    private sortBy: string | null = null;
    private sortDir: 'asc' | 'desc' = 'asc';
    /** The request behind the rows on screen; repeating it is not worth a round trip. */
    private lastSignature = '';
    /**
     * Counts requests issued, so only the newest answer is applied. A forced reload skips the signature check, so
     * two identical requests can be in flight at once and only a counter tells them apart.
     */
    private generation = 0;
    private filterTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private readonly source: ServerPagingSource<T>, options: ServerPagingOptions = {}) {
        this.pageSizeOptions = options.pageSizeOptions ?? [...DEFAULT_PAGE_SIZE_OPTIONS];
        this.pageSize = signal(options.pageSize ?? DEFAULT_PAGE_SIZE);
    }

    /** @param force true to load even when the request matches what is already on screen */
    load(force = false): void {
        const request: GridPageRequest = {
            offset: this.pageIndex() * this.pageSize(),
            limit: this.pageSize(),
            sortBy: this.sortBy ?? undefined,
            sort: this.sortDir,
            criteria: this.source.collectCriteria(),
            partialKeys: this.source.partialKeys?.() ?? []
        };
        const signature = JSON.stringify(request);
        if (!force && signature === this.lastSignature) {
            return;
        }
        this.lastSignature = signature;
        this.generation++;
        const requestId = this.generation;
        this.loading.set(true);
        this.source.fetchPage(request).subscribe({
            next: page => {
                if (requestId !== this.generation) {
                    return;
                }
                const rows = page?.results ?? [];
                const total = page?.total ?? rows.length;
                const lastPageIndex = Math.max(0, Math.ceil(total / Math.max(request.limit, 1)) - 1);
                this.totalRows.set(total);
                if (this.pageIndex() > lastPageIndex) {
                    // Rows were removed since the page was chosen, so it no longer exists; show the last that does.
                    this.pageIndex.set(lastPageIndex);
                    this.load();
                    return;
                }
                this.loading.set(false);
                this.source.onPage(rows, page);
            },
            error: err => {
                if (requestId !== this.generation) {
                    return;
                }
                // A failed request put nothing on screen, so the same request must be allowed to go out again.
                this.lastSignature = '';
                this.loading.set(false);
                this.source.onError?.(err);
            }
        });
    }

    goToPage(pageIndex: number): void {
        if (pageIndex < 0 || pageIndex > this.totalPages() - 1 || pageIndex === this.pageIndex()) {
            return;
        }
        this.pageIndex.set(pageIndex);
        this.load();
    }

    goToFirstPage(): void {
        this.goToPage(0);
    }

    goToPreviousPage(): void {
        this.goToPage(this.pageIndex() - 1);
    }

    goToNextPage(): void {
        this.goToPage(this.pageIndex() + 1);
    }

    goToLastPage(): void {
        this.goToPage(this.totalPages() - 1);
    }

    /** Starts from the first page: the page the user was on may not exist at the new size. */
    changePageSize(pageSize: number): void {
        if (pageSize === this.pageSize()) {
            return;
        }
        this.pageSize.set(pageSize);
        this.pageIndex.set(0);
        this.load();
    }

    /** Orders in the database rather than within the loaded page, from the first page. Null clears the sort. */
    setSort(sortBy: string | null, sortDir: 'asc' | 'desc'): void {
        this.sortBy = sortBy;
        this.sortDir = sortDir;
        this.pageIndex.set(0);
        this.load();
    }

    /** Re-queries from the first page once filter events stop arriving. */
    scheduleFilterRefresh(): void {
        this.clearFilterTimer();
        this.filterTimer = setTimeout(() => {
            this.filterTimer = null;
            this.pageIndex.set(0);
            this.load();
        }, ServerPagingController.FILTER_DEBOUNCE_MS);
    }

    /**
     * Forgets the page and the sort after a request the server refused, without asking again. The grid clears its
     * own rows and sort arrow.
     */
    abandonRejectedSearch(): void {
        this.sortBy = null;
        this.sortDir = 'asc';
        this.pageIndex.set(0);
        this.totalRows.set(0);
    }

    /** Drops a pending refresh and any answer still in flight; call from the owner's ngOnDestroy. */
    destroy(): void {
        this.clearFilterTimer();
        this.generation++;
    }

    private clearFilterTimer(): void {
        if (this.filterTimer) {
            clearTimeout(this.filterTimer);
            this.filterTimer = null;
        }
    }
}
